import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout.js";
import { useNavigate } from "react-router-dom";

const LOW_STOCK_THRESHOLD = 3;
const OUT_OF_STOCK_THRESHOLD = 0;

function getStockStatus(stock) {
  if (stock <= OUT_OF_STOCK_THRESHOLD) return { label: "Out of Stock", color: "#DC2626", bg: "#FFF0F0" };
  if (stock <= LOW_STOCK_THRESHOLD) return { label: "Low Stock", color: "#B45309", bg: "#FFF7E6" };
  return { label: "In Stock", color: "#15803D", bg: "#EDFDF4" };
}

export default function AdminInventoryScreen() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("stock_asc");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const res = await fetch("https://eleven11-i6v4.onrender.com/api/listings");
      const data = await res.json();
      setListings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch listings:", err);
    } finally {
      setLoading(false);
    }
  };

  /* One row per product — showing both new and used stock */
  const inventoryRows = listings.map(listing => {
    const newVariant = listing.variants?.find(v => v.condition === "new");
    const usedVariant = listing.variants?.find(v => v.condition === "used");
    const totalStock = (newVariant?.stock ?? 0) + (usedVariant?.stock ?? 0) +
      (!newVariant && !usedVariant ? (listing.unitsAvailable ?? 0) : 0);

    return {
      _id: listing._id,
      listingId: listing.listingId,
      title: listing.title,
      partNumber: listing.partNumber,
      category: listing.category,
      image: listing.images?.[0] || "",
      newStock: newVariant?.stock ?? null,
      newPrice: newVariant?.price ?? null,
      usedStock: usedVariant?.stock ?? null,
      usedPrice: usedVariant?.price ?? null,
      fallbackStock: !newVariant && !usedVariant ? (listing.unitsAvailable ?? 0) : null,
      totalStock
    };
  });

  /* Stats */
  const outOfStock = inventoryRows.filter(r => r.totalStock <= 0).length;
  const lowStock = inventoryRows.filter(r => r.totalStock > 0 && r.totalStock <= LOW_STOCK_THRESHOLD).length;
  const totalUnits = inventoryRows.reduce((sum, r) => sum + r.totalStock, 0);

  /* Filter */
  const filtered = inventoryRows.filter(row => {
    const matchSearch = !search ||
      row.title?.toLowerCase().includes(search.toLowerCase()) ||
      row.partNumber?.toLowerCase().includes(search.toLowerCase()) ||
      row.category?.toLowerCase().includes(search.toLowerCase());

    const matchStatus =
      filterStatus === "all" ||
      (filterStatus === "out" && row.totalStock <= 0) ||
      (filterStatus === "low" && row.totalStock > 0 && row.totalStock <= LOW_STOCK_THRESHOLD) ||
      (filterStatus === "in" && row.totalStock > LOW_STOCK_THRESHOLD);

    return matchSearch && matchStatus;
  });

  /* Sort */
  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "stock_asc":  return a.totalStock - b.totalStock;
      case "stock_desc": return b.totalStock - a.totalStock;
      case "title_asc":  return a.title?.localeCompare(b.title);
      case "title_desc": return b.title?.localeCompare(a.title);
      case "price_desc": return (b.newPrice || b.usedPrice || 0) - (a.newPrice || a.usedPrice || 0);
      case "price_asc":  return (a.newPrice || a.usedPrice || 0) - (b.newPrice || b.usedPrice || 0);
      default: return 0;
    }
  });

  return (
    <AdminLayout>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <p style={styles.kicker}>ELEVEN CONTROL CENTER</p>
          <h1 style={styles.title}>Inventory</h1>
        </div>
        <div style={styles.headerStats}>
          <div style={styles.statChip}>
            <span style={styles.statNum}>{inventoryRows.length}</span>
            <span style={styles.statLabel}>Products</span>
          </div>
          <div style={styles.statChip}>
            <span style={styles.statNum}>{totalUnits}</span>
            <span style={styles.statLabel}>Total Units</span>
          </div>
          <div style={{ ...styles.statChip, ...(lowStock > 0 ? styles.statChipWarn : {}) }}>
            <span style={{ ...styles.statNum, color: lowStock > 0 ? "#B45309" : "#0f0f17" }}>{lowStock}</span>
            <span style={styles.statLabel}>Low Stock</span>
          </div>
          <div style={{ ...styles.statChip, ...(outOfStock > 0 ? styles.statChipDanger : {}) }}>
            <span style={{ ...styles.statNum, color: outOfStock > 0 ? "#DC2626" : "#0f0f17" }}>{outOfStock}</span>
            <span style={styles.statLabel}>Out of Stock</span>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(outOfStock > 0 || lowStock > 0) && (
        <div style={styles.alertsSection}>
          {outOfStock > 0 && (
            <div style={styles.alertDanger}>
              <span style={styles.alertIcon}>⚠</span>
              <strong>{outOfStock} product{outOfStock !== 1 ? "s" : ""} out of stock</strong>
              — restock needed immediately
            </div>
          )}
          {lowStock > 0 && (
            <div style={styles.alertWarn}>
              <span style={styles.alertIcon}>↓</span>
              <strong>{lowStock} product{lowStock !== 1 ? "s" : ""} running low</strong>
              — {LOW_STOCK_THRESHOLD} or fewer units remaining
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div style={styles.tableSection}>

        {/* Toolbar */}
        <div style={styles.toolbar}>
          <input
            style={styles.search}
            placeholder="Search by name, part number, or category..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={styles.toolbarRight}>
            <div style={styles.filters}>
              {[
                { key: "all", label: "All" },
                { key: "out", label: "Out of Stock" },
                { key: "low", label: "Low Stock" },
                { key: "in",  label: "In Stock" }
              ].map(f => (
                <button
                  key={f.key}
                  style={{ ...styles.filterBtn, ...(filterStatus === f.key ? styles.filterBtnActive : {}) }}
                  onClick={() => setFilterStatus(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <select
              style={styles.sortSelect}
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="stock_asc">Stock: Low to High</option>
              <option value="stock_desc">Stock: High to Low</option>
              <option value="title_asc">Name: A → Z</option>
              <option value="title_desc">Name: Z → A</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="price_asc">Price: Low to High</option>
            </select>
          </div>
        </div>

        {/* Table Header */}
        <div style={styles.tableHeader}>
          <div style={{ width: 48 }} />
          <div style={{ flex: 1 }}>Product</div>
          <div style={{ width: 140, textAlign: "center" }}>New Stock</div>
          <div style={{ width: 140, textAlign: "center" }}>Used Stock</div>
          <div style={{ width: 130 }}>Status</div>
          <div style={{ width: 80 }}>Actions</div>
        </div>

        {/* Rows */}
        <div style={styles.tableBody}>
          {loading && <p style={styles.empty}>Loading inventory...</p>}
          {!loading && sorted.length === 0 && <p style={styles.empty}>No items found.</p>}

          {!loading && sorted.map((row, i) => {
            const status = getStockStatus(row.totalStock);
            return (
              <div
                key={`${row._id}-${i}`}
                style={{
                  ...styles.tableRow,
                  ...(row.totalStock <= 0 ? styles.tableRowDanger : row.totalStock <= LOW_STOCK_THRESHOLD ? styles.tableRowWarn : {})
                }}
              >
                {/* Image */}
                <div style={{ width: 48 }}>
                  {row.image ? (
                    <img src={`${row.image}`} alt={row.title} style={styles.rowImage} />
                  ) : (
                    <div style={styles.rowImagePlaceholder}>◫</div>
                  )}
                </div>

                {/* Title */}
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <p style={styles.rowTitle}>{row.title}</p>
                  {row.partNumber && (
                    <p style={styles.rowSub}>#{row.partNumber} · {row.category}</p>
                  )}
                </div>

                {/* New Stock */}
                <div style={{ width: 140, textAlign: "center" }}>
                  {row.newStock !== null ? (
                    <>
                      <p style={{ ...styles.rowStock, color: row.newStock <= 0 ? "#DC2626" : row.newStock <= LOW_STOCK_THRESHOLD ? "#B45309" : "#15803D" }}>
                        {row.newStock}
                      </p>
                      <p style={styles.rowStockLabel}>units · ${row.newPrice?.toFixed(2)}</p>
                    </>
                  ) : (
                    <p style={styles.rowStockLabel}>—</p>
                  )}
                </div>

                {/* Used Stock */}
                <div style={{ width: 140, textAlign: "center" }}>
                  {row.usedStock !== null ? (
                    <>
                      <p style={{ ...styles.rowStock, color: row.usedStock <= 0 ? "#DC2626" : row.usedStock <= LOW_STOCK_THRESHOLD ? "#B45309" : "#15803D" }}>
                        {row.usedStock}
                      </p>
                      <p style={styles.rowStockLabel}>units · ${row.usedPrice?.toFixed(2)}</p>
                    </>
                  ) : row.fallbackStock !== null ? (
                    <>
                      <p style={{ ...styles.rowStock, color: row.fallbackStock <= 0 ? "#DC2626" : row.fallbackStock <= LOW_STOCK_THRESHOLD ? "#B45309" : "#15803D" }}>
                        {row.fallbackStock}
                      </p>
                      <p style={styles.rowStockLabel}>units</p>
                    </>
                  ) : (
                    <p style={styles.rowStockLabel}>—</p>
                  )}
                </div>

                {/* Status */}
                <div style={{ width: 130 }}>
                  <span style={{ ...styles.statusBadge, background: status.bg, color: status.color }}>
                    {row.totalStock <= 0 ? "⚠ " : row.totalStock <= LOW_STOCK_THRESHOLD ? "↓ " : "✓ "}
                    {status.label}
                  </span>
                </div>

                {/* Actions */}
                <div style={{ width: 80 }}>
                  <button style={styles.editBtn} onClick={() => navigate(`/admin/create-listing/${row._id}`)}>
                    Edit
                  </button>
                </div>

              </div>
            );
          })}
        </div>

        <div style={styles.tableFooter}>
          Showing {sorted.length} of {inventoryRows.length} products
        </div>

      </div>

    </AdminLayout>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 },
  kicker: { margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "#9ca3af", textTransform: "uppercase" },
  title: { margin: "6px 0 0", fontSize: 36, fontWeight: 800, letterSpacing: -1, color: "#0f0f17" },
  headerStats: { display: "flex", gap: 12, paddingTop: 8 },
  statChip: { background: "#fff", borderRadius: 12, padding: "10px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
  statChipWarn: { background: "#FFF7E6", border: "1px solid #FDE68A" },
  statChipDanger: { background: "#FFF0F0", border: "1px solid #FECACA" },
  statNum: { fontSize: 22, fontWeight: 900, color: "#0f0f17", lineHeight: 1 },
  statLabel: { fontSize: 11, color: "#9ca3af", fontWeight: 600 },
  alertsSection: { display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 },
  alertDanger: { display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", background: "#FFF0F0", border: "1px solid #FECACA", borderRadius: 12, fontSize: 14, color: "#991B1B" },
  alertWarn: { display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", background: "#FFF7E6", border: "1px solid #FDE68A", borderRadius: 12, fontSize: 14, color: "#92400E" },
  alertIcon: { fontSize: 16, flexShrink: 0 },
  tableSection: { background: "#fff", borderRadius: 18, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)" },
  toolbar: { padding: "16px 24px", borderBottom: "1px solid #f3f4f6", display: "flex", gap: 12, alignItems: "center" },
  search: { flex: 1, padding: "10px 14px", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, outline: "none" },
  toolbarRight: { display: "flex", gap: 10, alignItems: "center" },
  filters: { display: "flex", gap: 6 },
  filterBtn: { padding: "7px 14px", border: "1px solid #e5e7eb", borderRadius: 999, background: "transparent", fontSize: 12, fontWeight: 600, color: "#6b7280", cursor: "pointer", whiteSpace: "nowrap" },
  filterBtnActive: { background: "#0f0f17", color: "#fff", border: "1px solid #0f0f17" },
  sortSelect: { padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 13, background: "#fff", outline: "none", cursor: "pointer", fontWeight: 600, color: "#374151" },
  tableHeader: { display: "flex", alignItems: "center", gap: 16, padding: "10px 24px", background: "#f9fafb", borderBottom: "1px solid #f3f4f6", fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.8 },
  tableBody: { maxHeight: "calc(100vh - 360px)", overflowY: "auto" },
  empty: { textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 },
  tableRow: { display: "flex", alignItems: "center", gap: 16, padding: "14px 24px", borderBottom: "1px solid #f9fafb", transition: "background 0.1s ease" },
  tableRowWarn: { background: "#fffbeb" },
  tableRowDanger: { background: "#fff5f5" },
  rowImage: { width: 44, height: 44, borderRadius: 10, objectFit: "cover", background: "#f3f4f6" },
  rowImagePlaceholder: { width: 44, height: 44, borderRadius: 10, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#d1d5db" },
  rowTitle: { margin: 0, fontSize: 13, fontWeight: 600, color: "#0f0f17", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowSub: { margin: "3px 0 0", fontSize: 12, color: "#9ca3af" },
  rowStock: { margin: 0, fontSize: 22, fontWeight: 900, lineHeight: 1 },
  rowStockLabel: { margin: "2px 0 0", fontSize: 11, color: "#9ca3af" },
  statusBadge: { display: "inline-block", padding: "5px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 },
  editBtn: { padding: "6px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer" },
  tableFooter: { padding: "12px 24px", borderTop: "1px solid #f3f4f6", fontSize: 13, color: "#9ca3af", fontWeight: 500 }
};